import { formatINR } from './currency';

export interface PackInfoSource {
  packSize?: string | null;
  qtyPerPack?: number | null;
  totalPackSize?: string | null;
  volumeLiters?: number | null;
  // Some catalogue items carry the pack only in their name ("…175M*80PCS").
  name?: string | null;
  productName?: string | null;
  description?: string | null;
  unit?: string | null;
}

export interface ParsedPackInfo {
  unitSize: number;
  unit: 'ML' | 'LTR' | 'G' | 'KG';
  qtyPerPack: number;
  totalBaseQuantity: number;
  baseUnit: 'LTR' | 'KG';
}

function normaliseUnit(unit: string): ParsedPackInfo['unit'] {
  const u = unit.toUpperCase();
  if (u === 'ML' || u === 'M') return 'ML';
  if (u === 'G' || u === 'GM' || u === 'GRAM') return 'G';
  if (u === 'KG') return 'KG';
  return 'LTR';
}

function toBase(value: number, unit: ParsedPackInfo['unit']) {
  if (unit === 'ML') return { value: value / 1000, unit: 'LTR' as const };
  if (unit === 'G') return { value: value / 1000, unit: 'KG' as const };
  if (unit === 'KG') return { value, unit: 'KG' as const };
  return { value, unit: 'LTR' as const };
}

function parseNumberUnit(text?: string | null) {
  // "175M" is how the catalogue writes 175 ML in product names.
  const match = String(text || '').match(/(\d+(?:\.\d+)?)\s*(LTR|LITRE|LITER|ML|KG|GRAM|GM|G|L|M)\b/i);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value) || value <= 0) return null;
  return { value, unit: normaliseUnit(match[2]) };
}

function parseCount(text?: string | null) {
  const raw = String(text || '');
  const match = raw.match(/[x*]\s*(\d+(?:\.\d+)?)\s*(?:NOS?|PCS?|PIECES?)?\b/i)
    || raw.match(/\b(\d+(?:\.\d+)?)\s*(?:NOS?|PCS?|PIECES?)\b/i);
  if (!match) return 0;
  const value = Number(match[1]);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function parsePackInfo(product?: PackInfoSource | null): ParsedPackInfo | null {
  if (!product) return null;
  // No pack fields at all: read the pack from the product's name instead.
  const nameText = product.productName || product.name || product.description || '';
  const packText = product.packSize || (!product.totalPackSize && !(Number(product.volumeLiters) > 0) ? nameText : '');
  let unitPack = parseNumberUnit(packText);
  let totalPack = parseNumberUnit(product.totalPackSize);
  const qtyPerPack = Number(product.qtyPerPack) > 1 ? Number(product.qtyPerPack) : parseCount(packText);

  // Fallback 1: If packText didn't include a recognized unit (e.g. packText was bare "210"),
  // check nameText which often contains "210 LTR", "26 LTR", etc.
  if (!unitPack && nameText) {
    unitPack = parseNumberUnit(nameText);
  }

  // Fallback 2: If still no unit found, but packText is a bare number (e.g. "210", 210):
  if (!unitPack && packText) {
    const numMatch = String(packText).trim().match(/^(\d+(?:\.\d+)?)$/);
    if (numMatch) {
      const numVal = Number(numMatch[1]);
      if (Number.isFinite(numVal) && numVal > 0) {
        // Detect if nameText or product.unit mentions KG/GM, otherwise default to LTR (for oils/fluids)
        const unitStr = String(product.unit || '').toUpperCase();
        const isKg = /\b(KG|KGS|KILOGRAM|GM|GRAM|G)\b/i.test(nameText) || unitStr === 'KG' || unitStr === 'G';
        unitPack = { value: numVal, unit: isKg ? 'KG' : 'LTR' };
      }
    }
  }

  // A "total" written as "500 GM x 24 (Box)" is ONE piece times a count, not the total.
  const totalCount = parseCount(product.totalPackSize);
  if (totalPack && totalCount > 1 && /[x*]/i.test(String(product.totalPackSize))) {
    totalPack = { value: totalPack.value * totalCount, unit: totalPack.unit };
  }

  if (totalPack) {
    const base = toBase(totalPack.value, totalPack.unit);
    const unitBase = unitPack ? toBase(unitPack.value, unitPack.unit) : null;
    return {
      unitSize: unitPack?.value || totalPack.value,
      unit: unitPack?.unit || totalPack.unit,
      qtyPerPack: qtyPerPack || (unitBase?.value ? Math.round((base.value / unitBase.value) * 1000) / 1000 : 1),
      totalBaseQuantity: base.value,
      baseUnit: base.unit,
    };
  }

  if (unitPack) {
    const base = toBase(unitPack.value, unitPack.unit);
    return {
      unitSize: unitPack.value,
      unit: unitPack.unit,
      qtyPerPack: qtyPerPack || 1,
      totalBaseQuantity: base.value * (qtyPerPack || 1),
      baseUnit: base.unit,
    };
  }

  if (Number(product.volumeLiters) > 0) {
    return {
      unitSize: Number(product.volumeLiters),
      unit: 'LTR',
      qtyPerPack: 1,
      totalBaseQuantity: Number(product.volumeLiters),
      baseUnit: 'LTR',
    };
  }

  return null;
}

function compact(n: number) {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
}

export function packSummary(product?: PackInfoSource | null) {
  const info = parsePackInfo(product);
  if (!info) return '';
  const pack = (typeof product?.packSize === 'string' ? product.packSize.trim() : (product?.packSize ? String(product.packSize) : '')) || `${compact(info.unitSize)} ${info.unit}`;
  const total = `${compact(info.totalBaseQuantity)} ${info.baseUnit}`;
  return `${pack}${info.qtyPerPack > 1 && !/[x*]/i.test(pack) ? ` x ${compact(info.qtyPerPack)}` : ''} = ${total}`;
}

export function totalBaseQuantityLabel(quantity: number, product?: PackInfoSource | null) {
  const info = parsePackInfo(product);
  if (!info) return '';
  return `${compact((Number(quantity) || 0) * info.totalBaseQuantity)} ${info.baseUnit}`;
}

export function ratePerBaseUnit(rate: number, product?: PackInfoSource | null) {
  if (!(Number(rate) > 0)) return '';
  const info = parsePackInfo(product);
  if (!info?.totalBaseQuantity || info.totalBaseQuantity <= 0) return '';
  // Sub-litre/gram consumer packs (e.g. 900 ML, 500 ML, grams) are priced
  // per pack/piece, not sold by the litre/kg. Converting them to /LTR creates
  // a confusing artificial rate that contradicts the actual quoted bottle price.
  // A single bottle/sachet under a litre/kg is priced per piece; a box of them
  // (175 ML x 80 = 14 LTR, 500 GM x 24 = 12 KG) is also quoted per LTR/KG.
  if (info.totalBaseQuantity < 1) return '';
  return `${formatINR((Number(rate) || 0) / info.totalBaseQuantity)}/${info.baseUnit}`;
}

/**
 * How many individual pieces make up one pack — "1" (never picked as a Rate
 * entry mode) unless qtyPerPack says otherwise. A line's Rate is always
 * stored as "price of one whole Qty-unit" (one pack); this is a pure
 * display/entry convenience for a rep who was quoted the price of ONE PIECE
 * instead, converted to the per-pack price the instant it's typed — never a
 * recurring transform on the stored value itself.
 */
export function piecesPerPack(product?: PackInfoSource | null): number {
  const n = Number(product?.qtyPerPack) || 0;
  if (n > 1) return n;
  const parsed = parsePackInfo(product)?.qtyPerPack || 0;
  return parsed > 1 ? parsed : 1;
}

/**
 * For a drum/barrel/bucket sold as ONE piece (piecesPerPack === 1) but filled
 * with a large weight/volume — e.g. a single 180 KG grease drum, or a single
 * 210 LTR barrel — a rep is often quoted a price per KG or per LTR rather
 * than a price for the whole container. Mirrors piecesPerPack's role for
 * that case: returns the container's own content quantity so a Rate cell can
 * offer a "per KG"/"per LTR" entry mode alongside "per piece", never instead
 * of it.
 */
export function baseUnitDivisor(product?: PackInfoSource | null): { qty: number; unit: string } | null {
  const info = parsePackInfo(product);
  if (!info || !(info.totalBaseQuantity > 1)) return null;
  return { qty: info.totalBaseQuantity, unit: info.baseUnit };
}
